import { useEffect, useState, lazy, Suspense } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Monitor, Smartphone, User, Wifi, Battery, Signal } from 'lucide-react';
import { eventBus } from '@/map/core/events';
import { pauseClock } from '@/map/core/pause';

interface ScreenData {
  image?: string;
  component?: string;
  device: 'desktop' | 'mobile';
  title: string;
  subtitle?: string;
  role?: string;
  url?: string;
  duration: number;
}

const componentRegistry: Record<string, React.LazyExoticComponent<any>> = {
  'tanker-dispatch': lazy(() => import('@/components/screens/TankerDispatchMock')),
  'mobile-notification': lazy(() => import('@/components/screens/MobileNotificationMock')),
  // Combined Solution — one live screen per scene (no static screenshots needed)
  'combined-problem': lazy(() => import('@/components/screens/CombinedProblemMock')),
  'combined-layer': lazy(() => import('@/components/screens/CombinedLayerMock')),
  'capital-delivery': lazy(() => import('@/components/screens/CapitalDeliveryMock')),
  'orp-allocation': lazy(() => import('@/components/screens/OrpAllocationMock')),
  'control-room': lazy(() => import('@/components/screens/ControlRoomMock')),
  'opus-permit': lazy(() => import('@/components/screens/OpusPermitMock')),
  'combined-close': lazy(() => import('@/components/screens/CombinedCloseMock')),
};

export function ProductScreenOverlay() {
  const [screen, setScreen] = useState<ScreenData | null>(null);

  useEffect(() => {
    const unsubShow = eventBus.on('screen:show', (data) => setScreen(data));
    const unsubHide = eventBus.on('screen:hide', () => setScreen(null));
    return () => { unsubShow(); unsubHide(); };
  }, []);

  return createPortal(
    <AnimatePresence>
      {screen && (
        <motion.div
          className="fixed inset-0 z-[95] flex items-center justify-center"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.4 }}
        >
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />

          <motion.div
            className="relative flex flex-col items-center gap-4"
            initial={{ scale: 0.92, y: 20 }}
            animate={{ scale: 1, y: 0 }}
            exit={{ scale: 0.95, y: 10 }}
            transition={{ duration: 0.4, ease: 'easeOut' }}
          >
            {/* Info bar above device */}
            <div className="flex items-center justify-between w-full px-2" style={{ maxWidth: screen.device === 'mobile' ? '320px' : '1100px' }}>
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-teal-500/15 border border-teal-500/30 flex items-center justify-center">
                  {screen.device === 'mobile' ? <Smartphone size={15} className="text-teal-400" /> : <Monitor size={15} className="text-teal-400" />}
                </div>
                <div>
                  <h3 className="text-white font-semibold text-sm">{screen.title}</h3>
                  {screen.subtitle && <p className="text-slate-400 text-[11px] mt-0.5">{screen.subtitle}</p>}
                </div>
              </div>
              {screen.role && (
                <div className="flex items-center gap-1.5 px-3 py-1.5 bg-slate-800/80 border border-slate-600/30 rounded-lg">
                  <User size={11} className="text-teal-400" />
                  <span className="text-[10px] font-medium text-slate-300">{screen.role}</span>
                </div>
              )}
            </div>

            {/* Device frame */}
            {screen.device === 'desktop' ? (
              <DesktopFrame url={screen.url} duration={screen.duration}>
                <ScreenContent screen={screen} />
              </DesktopFrame>
            ) : (
              <MobileFrame duration={screen.duration}>
                <ScreenContent screen={screen} />
              </MobileFrame>
            )}

            {/* Bottom label */}
            <div className="flex items-center gap-2 text-[10px] text-slate-500 uppercase tracking-widest">
              <div className="w-1.5 h-1.5 rounded-full bg-teal-500 animate-pulse" />
              <span>{screen.device === 'mobile' ? 'Mobile App View' : 'Web Platform View'}</span>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body
  );
}

/** Screenshot countdown bar — freezes while the scenario is paused
 *  (driven by the global pause clock, same as everything else). */
function PausableProgress({ duration }: { duration: number }) {
  const [pct, setPct] = useState(100);
  useEffect(() => {
    let remaining = duration;
    const STEP = 50;
    const id = setInterval(() => {
      if (pauseClock.paused) return;
      remaining -= STEP;
      setPct(Math.max(0, (remaining / duration) * 100));
      if (remaining <= 0) clearInterval(id);
    }, STEP);
    return () => clearInterval(id);
  }, [duration]);
  return (
    <div className="absolute bottom-0 left-0 right-0 h-[3px] bg-white/5">
      <div
        className="h-full bg-teal-500/80"
        style={{ width: `${pct}%`, transition: 'width 60ms linear' }}
      />
    </div>
  );
}

function DesktopFrame({ url, duration, children }: { url?: string; duration: number; children: React.ReactNode }) {
  return (
    <div className="w-[75vw] max-w-[1100px] rounded-xl overflow-hidden shadow-2xl shadow-black/60 border border-slate-700/50">
      {/* Browser chrome */}
      <div className="bg-slate-800 px-3 py-2 flex items-center gap-3 border-b border-slate-700/50">
        {/* Traffic lights */}
        <div className="flex gap-1.5">
          <div className="w-2.5 h-2.5 rounded-full bg-red-500/80" />
          <div className="w-2.5 h-2.5 rounded-full bg-yellow-500/80" />
          <div className="w-2.5 h-2.5 rounded-full bg-green-500/80" />
        </div>
        {/* Address bar */}
        <div className="flex-1 bg-slate-900/80 rounded-md px-3 py-1 flex items-center gap-2">
          <svg width="10" height="10" viewBox="0 0 10 10" className="text-slate-500"><path d="M5 1L5 5M5 5L8 5M5 5L2 5M5 5L5 9" stroke="currentColor" strokeWidth="1.2"/></svg>
          <span className="text-[11px] text-slate-400 font-mono">{url || 'app.skewb.io/control-room'}</span>
        </div>
      </div>
      {/* Content area */}
      <div className="relative bg-slate-900 max-h-[70vh] overflow-hidden">
        {children}
        <PausableProgress duration={duration} />
      </div>
    </div>
  );
}

function MobileFrame({ duration, children }: { duration: number; children: React.ReactNode }) {
  return (
    <div className="relative w-[300px] rounded-[2.5rem] overflow-hidden shadow-2xl shadow-black/60 border-[3px] border-slate-700 bg-black p-1">
      {/* Inner frame */}
      <div className="rounded-[2.2rem] overflow-hidden">
        {/* Status bar */}
        <div className="bg-slate-900 px-5 py-1.5 flex items-center justify-between">
          <span className="text-[10px] text-white font-medium">9:41</span>
          <div className="absolute left-1/2 -translate-x-1/2 w-20 h-5 bg-black rounded-b-xl" />
          <div className="flex items-center gap-1">
            <Signal size={10} className="text-white" />
            <Wifi size={10} className="text-white" />
            <Battery size={10} className="text-white" />
          </div>
        </div>
        {/* Content */}
        <div className="relative bg-slate-900 h-[560px] overflow-hidden">
          {children}
          <PausableProgress duration={duration} />
        </div>
        {/* Home indicator */}
        <div className="bg-slate-900 py-2 flex justify-center">
          <div className="w-28 h-1 rounded-full bg-slate-600" />
        </div>
      </div>
    </div>
  );
}

function ScreenContent({ screen }: { screen: ScreenData }) {
  if (screen.image) {
    return <img src={screen.image} alt={screen.title} className="w-full h-full object-cover object-top" />;
  }
  if (screen.component && componentRegistry[screen.component]) {
    const Component = componentRegistry[screen.component];
    return (
      <Suspense fallback={<div className="w-full h-full bg-slate-900 animate-pulse" />}>
        <Component />
      </Suspense>
    );
  }
  return <div className="w-full h-full bg-slate-900 flex items-center justify-center text-slate-500">No content</div>;
}
